const TIME_CONSTANTS = {
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
  YEAR: 365 * 24 * 60 * 60 * 1000,
} as const;

const PLUGGY_CLIENT_ID = process.env.PLUGGY_CLIENT_ID || '';
const PLUGGY_CLIENT_SECRET = process.env.PLUGGY_CLIENT_SECRET || '';

const PREMIUM_PLAN_LIMIT = 20;
const PRO_PLAN_LIMIT = 5;
const FREE_PLAN_LIMIT = 1;

const AMPLITUDE_USER_ID_COOKIE_NAME = 'rl_user_id';
const AMPLITUDE_ANONYMOUS_ID_COOKIE_NAME = 'rl_anonymous_id';

const LEGACY_AVATAR_LAST_VARIANT = 18;
const SELECTABLE_AVATAR_FIRST_VARIANT = 19;
const SELECTABLE_AVATAR_LAST_VARIANT = 57;

type AvatarFilename = `${number}.png` | `${number}.jpg`;

const LEGACY_AVATARS = Array.from(
  { length: LEGACY_AVATAR_LAST_VARIANT },
  (_, index) => `${index + 1}.png` as AvatarFilename,
);

const AVATARS = Array.from(
  {
    length:
      SELECTABLE_AVATAR_LAST_VARIANT - SELECTABLE_AVATAR_FIRST_VARIANT + 1,
  },
  (_, index) =>
    `${SELECTABLE_AVATAR_FIRST_VARIANT + index}.jpg` as AvatarFilename,
);

const SUPPORTED_AVATARS = [...LEGACY_AVATARS, ...AVATARS] as const;
const DEFAULT_SELECTABLE_AVATAR = AVATARS[0] as AvatarFilename;

const supportedAvatarSet = new Set<string>(SUPPORTED_AVATARS);
const selectableAvatarSet = new Set<string>(AVATARS);

const isValidAvatar = (
  value: string | undefined | null,
): value is AvatarFilename => {
  return typeof value === 'string' && supportedAvatarSet.has(value);
};

const isSelectableAvatar = (
  value: string | undefined | null,
): value is AvatarFilename => {
  return typeof value === 'string' && selectableAvatarSet.has(value);
};

const getRandomAvatar = (excludeAvatar?: string): string => {
  let newAvatar = AVATARS[Math.floor(Math.random() * AVATARS.length)];

  const normalizedExcludedAvatar = isSelectableAvatar(excludeAvatar)
    ? excludeAvatar
    : undefined;

  while (newAvatar === normalizedExcludedAvatar && AVATARS.length > 1) {
    newAvatar = AVATARS[Math.floor(Math.random() * AVATARS.length)];
  }
  return newAvatar;
};

const getAvatarUrl = (
  avatarValue: string | null | undefined,
  fallbackImage?: string | null,
): string | undefined => {
  if (!avatarValue) {
    return fallbackImage ?? undefined;
  }

  if (avatarValue.startsWith('http://') || avatarValue.startsWith('https://')) {
    return avatarValue;
  }

  if (avatarValue.startsWith('/images/avatars/')) {
    return avatarValue;
  }

  const normalizedAvatar = avatarValue.replace(/^avatar-/, '');

  if (isValidAvatar(normalizedAvatar)) {
    return `/images/avatars/${normalizedAvatar}`;
  }

  // Unknown value, use fallback
  return fallbackImage ?? undefined;
};

export {
  TIME_CONSTANTS,
  PLUGGY_CLIENT_ID,
  PLUGGY_CLIENT_SECRET,
  PREMIUM_PLAN_LIMIT,
  PRO_PLAN_LIMIT,
  FREE_PLAN_LIMIT,
  AMPLITUDE_USER_ID_COOKIE_NAME,
  AMPLITUDE_ANONYMOUS_ID_COOKIE_NAME,
  AVATARS,
  LEGACY_AVATARS,
  SUPPORTED_AVATARS,
  DEFAULT_SELECTABLE_AVATAR,
  isValidAvatar,
  isSelectableAvatar,
  getRandomAvatar,
  getAvatarUrl,
  type AvatarFilename,
};
